import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TYPE_FACEBOOK_AD } from '@kontentino/kontentino-constants/Pages';
import { useListings } from 'modules/listings/listingsSelectors';
import { UserPermission } from 'constants/userPermission';
import UserPermissionGate from 'components/UserPermissionGate';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons/faCircleQuestion';
import {
  Button,
  Checkbox,
  Field,
  Icon,
  Select,
  TextInput,
  Tooltip,
} from '@kontentino/ui';
import Modal from 'components/shared/Modal';
import { useTranslation } from 'react-i18next';

type Props = {
  pageType: number;
  onSubmit(values: FormState): void;
  onClose: () => void;
};

type FormState = {
  name: string;
  postType: { label: string; value: number } | null;
  isGlobal: boolean;
};

const NewPostRequirementModalForm: FC<Props> = (props) => {
  const { postTypes } = useListings();
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormState>({
    defaultValues: { postType: null },
  });
  const { t } = useTranslation();

  return (
    <form onSubmit={handleSubmit(props.onSubmit)}>
      <Modal.Content className="tw-flex tw-flex-col tw-gap-6">
        <Field.Group>
          <Field.Label required>{t('name')}</Field.Label>
          <TextInput {...register('name', { required: t('isRequired') })} />
          <Field.Error>{errors.name?.message}</Field.Error>
        </Field.Group>
        {props.pageType !== TYPE_FACEBOOK_AD && (
          <Field.Group className="tw-mb-px">
            <Field.Label required>{t('postType')}</Field.Label>
            <Controller
              name="postType"
              control={control}
              rules={{ required: t('isRequired') }}
              render={({ field: { value, onChange } }) => {
                return (
                  <Select
                    value={value}
                    onChange={(option) => onChange(option)}
                    options={
                      postTypes[props.pageType]?.map((type) => ({
                        label: type.name,
                        value: type.id,
                      })) || []
                    }
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                  />
                );
              }}
            />
            <Field.Error>{errors.postType?.message}</Field.Error>
          </Field.Group>
        )}
        <UserPermissionGate scopes={UserPermission.REQUIREMENT_MANAGE_GLOBAL}>
          <Controller
            control={control}
            defaultValue={false}
            name="isGlobal"
            render={({ field: { value, onChange } }) => (
              <Field.Group>
                <Field.Label>{t('sharing')}</Field.Label>
                <div className="tw-flex tw-items-center">
                  <Checkbox
                    checked={value}
                    size="small"
                    onChange={() => onChange(!value)}
                  />
                  <Field.Label
                    className="tw-ml-2 tw-flex tw-cursor-pointer tw-items-center"
                    onClick={() => onChange(!value)}
                    required
                  >
                    {t('isGlobal')}
                    <Tooltip content={t('globalRequirementTooltip')}>
                      <Icon icon={faCircleQuestion} marginLeft={8} />
                    </Tooltip>
                  </Field.Label>
                </div>
              </Field.Group>
            )}
          />
        </UserPermissionGate>
      </Modal.Content>
      <Modal.Footer>
        <Button variant="ghost" onClick={props.onClose}>
          {t('cancel')}
        </Button>
        <Button type="submit">{t('save')}</Button>
      </Modal.Footer>
    </form>
  );
};

export default NewPostRequirementModalForm;
